// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// Synthesizes the same `{ records, hierarchy, totalRespondents, totalDocs }`
// shape that `useHierarchyData` returns from the Cloud Function — but built
// directly from the pre-fetched template-data blob (`exportTemplateData`
// response). Used in widget template mode so the chart leaves stop seeing
// 0-records and pick the right moment-vs-development variant.
//
// Supports the same syncTypes as fetchHierarchyData:
//   - 'Question' / 'MSIEffect' → QuestionnairesResponses by FieldID
//   - 'Event'                  → EventResults by FieldID
//   - 'OutputEvent'            → EventResults by OutputID
//
// In template mode there's only one project (the Stichting Template tenant),
// so hierarchy collapses to a single-node tree.

const TEMPLATE_PROJECT_NAME = 'Stichting Template'

/**
 * @param {object} args
 * @param {'Question'|'MSIEffect'|'Event'|'OutputEvent'} args.syncType
 * @param {string} args.id - FieldID (Question/MSIEffect/Event) or OutputID (OutputEvent)
 * @param {object} args.templateData - The `data` blob from exportTemplateData (i.e. `templateContext.templateData.data`)
 * @param {string|null} [args.personaId] - Alleen deze deelnemer; zelfde filter als fetchHierarchyData
 * @returns {null|{records: object[], hierarchy: object, respondentsPerProject: object, totalRespondents: number, totalDocs: number}}
 */
export const synthesizeTemplateHierarchyData = ({ syncType, id, templateData, personaId = null }) => {
    if (!templateData || !id) return null

    const data = templateData.data || templateData

    /** @type {object[]} */
    let raw = []
    let momentIndexKey = 'MomentID'

    if (syncType === 'Question' || syncType === 'MSIEffect') {
        raw = (data?.effects?.responses || []).filter(r => r.FieldID === id)
    } else if (syncType === 'Event') {
        raw = (data?.outputs?.eventResults || []).filter(r => r.FieldID === id)
    } else if (syncType === 'OutputEvent') {
        raw = (data?.outputs?.eventResults || []).filter(r => r.OutputID === id)
    } else {
        return null
    }

    // Zelfde regel als buildRecordQuery in functions/data/bfsTraversal.js:
    // een output kent zijn deelnemers via `Participants`, een antwoord via `Persona`.
    // Bewaakt door functions/data/templatePersonaFilter.parity.test.js.
    // AI-CONTEXT: See docs/features/livereport-templates-deelnemer.md (T4)
    if (personaId) {
        raw = syncType === 'OutputEvent'
            ? raw.filter(r => Array.isArray(r.Participants) && r.Participants.includes(personaId))
            : raw.filter(r => r.Persona === personaId)
    }

    // Build a MomentID → Position lookup from MeasureMoments so records carry
    // `MomentPosition` (downstream chart code groups by this). Skip soft-deleted
    // moments (ADR-008) so template mode matches live mode: their records get no
    // MomentPosition and drop out of the position grouping.
    const moments = data?.effects?.measureMoments || []
    const momentPositionById = {}
    for (const m of moments) {
        if (m?.Deleted) continue
        if (m?.ID && m.Position != null) {
            momentPositionById[m.ID] = typeof m.Position === 'number'
                ? m.Position
                : parseInt(m.Position, 10)
        }
    }

    const respondentKey = (syncType === 'Event' || syncType === 'OutputEvent')
        ? 'SessionID'
        : 'FormID'

    const respondentSet = new Set()
    const records = raw.map(r => {
        const out = { ...r }
        const mid = r[momentIndexKey]
        if (mid != null && momentPositionById[mid] != null) {
            out.MomentPosition = momentPositionById[mid]
        }
        out._projectName = TEMPLATE_PROJECT_NAME
        out._hierarchyPath = '0'
        const respondentId = r[respondentKey]
        if (respondentId != null) respondentSet.add(respondentId)
        return out
    })

    const totalRespondents = respondentSet.size || records.length

    const hierarchy = {
        [TEMPLATE_PROJECT_NAME]: {
            depth: 0,
            path: '0',
            totalDocs: records.length,
            totalResponses: records.length,
            totalRespondents,
        },
    }

    return {
        records,
        hierarchy,
        respondentsPerProject: { [TEMPLATE_PROJECT_NAME]: totalRespondents },
        totalRespondents,
        totalDocs: records.length,
    }
}
